import type { StoreSettings } from '@nsd/core';
import { useEffect, useState, type FormEvent } from 'react';
import { ErrorBox, Loading, Panel, api, useAction, useLoad, when } from '../ui.tsx';

const FIELDS: { key: keyof StoreSettings; label: string; help: string; type?: string }[] = [
  { key: 'etransferRecipientEmail', label: 'e-Transfer recipient email', help: 'Shown to every customer on their order page. Change it before sharing the site.', type: 'email' },
  { key: 'etransferRecipientName', label: 'e-Transfer recipient name', help: 'The account holder name customers will see in their bank app (optional).' },
  { key: 'notificationEmail', label: 'Notification email', help: 'Where new-order emails will go once email sending is set up.', type: 'email' },
  { key: 'announcement', label: 'Announcement bar', help: 'The red bar at the top of every page. Leave empty to hide it. Shows after the next site build.' },
  { key: 'closedMessage', label: 'Message when orders are closed', help: 'Shown at checkout when ordering is switched off.' },
];

export function Settings() {
  const { data, error, loading } = useLoad(() => api.config());
  const [form, setForm] = useState<StoreSettings | null>(null);
  const action = useAction();
  const [saved, setSaved] = useState(false);
  useEffect(() => setForm(data), [data]);
  if (loading && !form) return <Loading />;
  if (!form) return <ErrorBox error={error ?? 'Store settings have not been created yet.'} />;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    setSaved(false);
    void action.run(async () => {
      setForm(await api.saveConfig(form));
      setSaved(true);
    });
  };
  return (
    <>
      <h1>Settings</h1>
      <form onSubmit={submit}>
        <Panel title="Store">
          <ErrorBox error={action.error} />
          {saved && <div className="notice notice--ok">Saved.</div>}
          <div className="form">
            {FIELDS.map((f) => (
              <label key={f.key}>
                {f.label}
                <input type={f.type ?? 'text'} value={String(form[f.key] ?? '')} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} />
                <small className="muted">{f.help}</small>
              </label>
            ))}
            <label className="check">
              <input type="checkbox" checked={form.ordersOpen} onChange={(e) => setForm({ ...form, ordersOpen: e.target.checked })} />
              Accepting orders
            </label>
          </div>
          <button className="btn" disabled={action.busy}>Save settings</button>
          <p className="muted">Last saved {when(form.updatedAt)}</p>
        </Panel>
      </form>
    </>
  );
}
